import debounce from 'debounce-promise';

export const tablesInitialState = {};

const tableObjectInitialState = {
  // internal state
  isLoading: true,

  // external state
  items: null, // the array of data
  itemsReturned: null, // the count of items returned
  rowsPerPage: 50, // the default page limit
  currentPage: 1,
  totalItemsFound: 0,
  firstPageOffset: 0,
  lastPageOffset: 0,
  nextPageOffset: 0,
  totalPagesFound: 0,

  // configurable state
  filter: null,
  sortBy: null,
  sortDirection: null,
};

const onLoadDatas = {}; // internal storage of data returning functions, keyed by tableId

export const tablesActions = (store) => {
  const setState = ({ state, tableId, values }) => {
    store.setState({
      ...state,
      ui: {
        ...state.ui,
        tables: {
          ...state.ui.tables,
          [tableId]: {
            ...(state.ui.tables[tableId] || tableObjectInitialState),
            ...values,
          },
        },
      },
    });
  };

  const loadDataNow = async ({ tableId }) => {
    let state = store.getState();
    const tableState = state.ui.tables[tableId] || tableObjectInitialState;
    const recordSet = await onLoadDatas[tableId]({
      filter: tableState.filter,
      sortBy: tableState.sortBy,
      sortDirection: tableState.sortDirection,
      limit: tableState.rowsPerPage,
      offset: (tableState.currentPage - 1) * tableState.rowsPerPage,
    });

    // get new state now we've waited to check if the request is still current
    let oldState = state.ui.tables[tableId];
    let newState = store.getState().ui.tables[tableId];
    if (
      oldState.rowsPerPage !== newState.rowsPerPage ||
      oldState.currentPage !== newState.currentPage ||
      JSON.stringify(oldState.filter) !== JSON.stringify(newState.filter) ||
      oldState.sortBy !== newState.sortBy ||
      oldState.sortDirection !== newState.sortDirection
    ) {
      // One of our request parameters has changed, bail
      return;
    }

    if (
      !recordSet ||
      !Array.isArray(recordSet.items) ||
      typeof recordSet.itemsReturned !== 'number'
    ) {
      throw Error(
        'Table.props.onLoadData() expected to return { items: [], itemsReturned: 0 }, got: ' +
          JSON.stringify(recordSet),
      );
    }

    const {
      items,
      itemsReturned,
      totalItemsFound,
      firstPageOffset,
      lastPageOffset,
      nextPageOffset,
      totalPagesFound,
    } = recordSet;

    state = store.getState(); // reload state after awaiting
    return {
      ui: {
        ...state.ui,
        tables: {
          ...state.ui.tables,
          [tableId]: {
            ...tableState,
            isLoading: false,
            items,
            itemsReturned,
            totalItemsFound,
            firstPageOffset,
            lastPageOffset,
            nextPageOffset,
            totalPagesFound,
          },
        },
      },
    };
  };

  const loadData = debounce(loadDataNow, 250);

  return {
    bindTable: async (state, { tableId, onLoadData }) => {
      onLoadDatas[tableId] = onLoadData;
      setState({
        state,
        tableId,
        values: state.ui.tables[tableId] || tableObjectInitialState,
      });

      return await loadDataNow({ tableId });
    },
    resetTable: async (state, { tableId }) => {
      setState({
        state,
        tableId,
        values: tableObjectInitialState,
      });
    },
    resetTables: (state) => {
      return store.setState({
        ...state,
        ui: {
          ...state.ui,
          tables: {
            ...tablesInitialState,
          },
        },
      });
    },
    setTableFilter: async (state, { tableId, filter = {} }) => {
      setState({
        state,
        tableId,
        values: {
          isLoading: true,
          filter,
          currentPage: 1,
        },
      });
      return await loadData({ tableId });
    },
    setTableOrder: async (state, { tableId, sortBy = null, sortDirection = null }) => {
      setState({
        state,
        tableId,
        values: {
          isLoading: true,
          sortBy,
          sortDirection,
          currentPage: 1,
        },
      });
      return await loadData({ tableId });
    },
    setTableRowsPerPage: async (state, { tableId, rowsPerPage }) => {
      setState({
        state,
        tableId,
        values: {
          isLoading: true,
          rowsPerPage,
          currentPage: 1,
        },
      });
      return await loadData({ tableId });
    },
    setTablePage: async (state, { tableId, currentPage }) => {
      setState({
        state,
        tableId,
        values: {
          isLoading: true,
          currentPage,
        },
      });
      return await loadData({ tableId });
    },
  };
};
